import {
  ActionIcon, Collapse,
  Divider,
  Flex,
  Paper,
  Text,
  Button,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {

  useMemo, useState, useEffect
} from "react";

/* eslint-disable react-hooks/exhaustive-deps */
import {
  ChevronUpIcon,
} from "@heroicons/react/20/solid";
import uuid from "react-uuid";
import { useTraitsAndMetrics } from "../../../../hooks/QueryHooks";
import MButton from "../../../../shared/ui/buttons/MButton";
import MultiSelectDropdown from "../../../../shared/ui/dropdown/MultiSelectDropdown";
// HeaderTrait no longer used in this component
import useWorkspaceStore from "../../../../store/workspace/workspaceStore";
import { getAccountPinnedTraits, updateAccountPinnedTraits } from "../../../../store/preference/preferencesHelpers";
import usePreferencesStore from "../../../../store/preference/preferencesStore";
import { PencilIcon } from "lucide-react";
import TruncatedTextTooltip from "../../../../shared/ui/truncate/TruncatedText";

type Props = {
  account: any,
  defaultOpen?: boolean,
  keyRolesComponent?: React.ReactNode,
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  withBorder?: boolean;
  withTitle?: boolean;
};

const HqAccountTraits = ({ account, defaultOpen = true, keyRolesComponent, size = 'sm', withBorder = true, withTitle = true }: Props) => {
  const [opened, handlers] = useDisclosure(defaultOpen);
  const [showTraitDropdownOpen, setShowTraitDropdownOpen] = useState(false);
  const [showAllTraits, { toggle: toggleShowAllTraits }] = useDisclosure(false);

  const workspaceId = useWorkspaceStore((state) => state.workspace?.id);

  // Get preferences from the store
  const preferences = usePreferencesStore((state) => state.preferences);

  // Initialize displayed traits from preferences or use an empty array
  const [displayedTraits, setDisplayedTraits] = useState<string[]>([]);

  // Update displayed traits when preferences load
  useEffect(() => {
    const accountTraits = getAccountPinnedTraits();
    if (accountTraits && accountTraits.length > 0) {
      setDisplayedTraits(accountTraits);
    }
  }, [preferences]);

  const onSelectTrait = async (trait: any) => {
    const newTraits = displayedTraits.includes(trait.name)
      ? displayedTraits.filter((name: any) => name !== trait.name)
      : [...displayedTraits, trait.name];

    const previousTraits = [...displayedTraits]; // Store before optimistic update
    setDisplayedTraits(newTraits); // Optimistic update

    try {
      await updateAccountPinnedTraits(newTraits);
      // Optionally close dropdown on successful selection, if desired
      // setShowTraitDropdownOpen(false);
    } catch (error) {
      console.error('Failed to update account traits:', error);
      setDisplayedTraits(previousTraits); // Revert on failure
      // TODO: Notify user of the error (e.g., using a toast notification)
    }
  };

  const { data }: any = useTraitsAndMetrics({
    workspaceId,
    enabled: !!workspaceId,
  });

  const shownTraits = useMemo(() => Object
    .entries(account || {}).reduce((obj: any, [trKey, trValue]: any) => {
      if (displayedTraits?.some((item: any) => item === `accounts.${trKey}`)) {
        const selectedTrait = data?.accountTraits?.obj[`accounts.${trKey}`];
        if (selectedTrait && !selectedTrait.exclude_display) {
          obj[trKey] = {
            ...selectedTrait,
            val: trValue,
          };
        }
      }
      return obj;
    }, {}), [
    displayedTraits,
    data?.accountTraits?.obj, account]);

  return (
    <Paper shadow={withBorder ? "sm" : "none"} p={withTitle ? "xs" : "0"} radius="md" className={withBorder ? "border border-gray-200" : ""} style={{ position: "relative" }}>
      {withTitle ? (
        <Flex align="center" p="xs" className="border-b border-gray-200" justify="space-between">
          <Text size="xs" lts={0.6} c="text.5" tt="uppercase" fw={600}>
            TRAITS
          </Text>
          <Flex gap={5} align="center">
            <MultiSelectDropdown
              selected={displayedTraits}
              position="bottom-end"
              setSelected={onSelectTrait}
              isOpen={showTraitDropdownOpen}
              setIsOpen={setShowTraitDropdownOpen}
              list={data?.accountTraits?.list?.filter((trait: any) => !trait.exclude_display) || []}
            >
              <div>
                <MButton color="white" size="xs" variant="outline" radius="sm" leftSection={<PencilIcon size={14} />}>
                  Edit Traits
                </MButton>
              </div>
            </MultiSelectDropdown>
            <ActionIcon onClick={handlers.toggle} variant="light" color="text.5" size="xs">
              <ChevronUpIcon style={{ transform: opened ? "rotate(0)" : "rotate(180deg)" }} />
            </ActionIcon>
          </Flex>
        </Flex>
      ) : (
        <Divider
          label={
            <div className="flex items-center gap-2">
              Account Traits
              <MultiSelectDropdown
                selected={displayedTraits}
                position="bottom-start"
                setSelected={onSelectTrait}
                isOpen={showTraitDropdownOpen}
                setIsOpen={setShowTraitDropdownOpen}
                list={data?.accountTraits?.list?.filter((trait: any) => !trait.exclude_display) || []}
              >
                <PencilIcon className="cursor-pointer w-3 h-3" />
              </MultiSelectDropdown>
            </div>
          }
          labelPosition="center"
        />
      )}
      {withTitle && <Collapse in={opened}>
        <div className="px-4 py-2">
          {(Object.entries(shownTraits).slice(0, 5)).map(([, value]: [string, any]) => (
            <div key={uuid()} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0">
              <Text size={size} c="gray.6">{value.display_name}</Text>
              <TruncatedTextTooltip copy width={350} size={size} fw={500} c="gray.8">{value.val !== undefined ? value.val : 'Empty'}</TruncatedTextTooltip>
            </div>
          ))}
          <Collapse in={showAllTraits}>
            {(Object.entries(shownTraits).slice(5)).map(([, value]: [string, any]) => (
              <div key={uuid()} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0">
                <Text size={size} c="gray.6">{value.display_name}</Text>
                <TruncatedTextTooltip copy width={150} size={size} fw={500} c="gray.8">{value.val !== undefined ? value.val : 'Empty'}</TruncatedTextTooltip>
              </div>
            ))}
          </Collapse>
          {Object.entries(shownTraits).length > 5 && (
            <div className="flex justify-center mt-2">
              <Button
                variant="subtle"
                size="xs"
                onClick={toggleShowAllTraits}
              >
                {showAllTraits ? 'See less' : `See more (${Object.entries(shownTraits).length - 5})`}
              </Button>
            </div>
          )}
        </div>
        {keyRolesComponent && (
          <div>
            <Divider className="my-0" />
            <Text size="xs" lts={0.6} c="text.5" tt="uppercase" fw={600} className="px-4 pt-3 pb-2">KEY ROLES</Text>
            <div className="px-4 pb-2">
              {keyRolesComponent}
            </div>
          </div>
        )}
      </Collapse>}
      {!withTitle && (
        <>
          <div>
            {(Object.entries(shownTraits).slice(0, 5)).map(([, value]: [string, any]) => (
              <div key={uuid()} className="flex justify-between items-center py-2 border-b border-dashed border-gray-100 last:border-b-0">
                <Text size={size} c="gray.6">{value.display_name}</Text>
                <TruncatedTextTooltip copy width={150} size={size} fw={500} c="gray.8">{value.val !== undefined ? value.val : 'Empty'}</TruncatedTextTooltip>
              </div>
            ))}
            <Collapse in={showAllTraits}>
              {(Object.entries(shownTraits).slice(5)).map(([, value]: [string, any]) => (
                <div key={uuid()} className="flex justify-between items-center py-2 border-b border-dashed border-gray-100 last:border-b-0">
                  <Text size={size} c="gray.6">{value.display_name}</Text>
                  <TruncatedTextTooltip copy width={150} size={size} fw={500} c="gray.8">{value.val !== undefined ? value.val : 'Empty'}</TruncatedTextTooltip>
                </div>
              ))}
            </Collapse>
            {Object.entries(shownTraits).length > 5 && (
              <div className="flex justify-center mt-2">
                <Button
                  variant="subtle"
                  size="xs"
                  onClick={toggleShowAllTraits}
                >
                  {showAllTraits ? 'See less' : `See more (${Object.entries(shownTraits).length - 5})`}
                </Button>
              </div>
            )}
          </div>
          {keyRolesComponent && (
            <div>
              <Divider className="my-0" />
              <Text size="xs" lts={0.6} c="text.5" tt="uppercase" fw={600} className="px-4 pt-3 pb-2">KEY ROLES</Text>
              <div className="px-4 pb-2">
                {keyRolesComponent}
              </div>
            </div>
          )}
        </>
      )}
    </Paper>
  );
};

export default HqAccountTraits;
